import jwt_decode from "jwt-decode";
import history from "./history";
import urls from "../global/constants/UrlConstants";
import { store } from "./store";
import { logOutAction, updateTokens } from "../redux/authSlice";
import strings from "../global/constants/StringConstants";
import NotificationConstants from "../global/constants/NotificationConstants";
import notifiers from "../global/constants/NotificationConstants";
import { api } from "./interceptor";
import posthog from "posthog-js";

export async function getCallParams(
  method: string,
  passedHeaders?: any,
  body?: any
) {
  let auth = store.getState().auth;
  let menu = store.getState().menu;

  try {
    const headers = {
      ...(await getHeaderObject({ ...auth, sessionId: menu.sessionId })),
      ...passedHeaders,
    };
    return { method, headers, data: JSON.stringify(body) };
  } catch (error: any) {
    if (
      error?.message === "jwt expired" ||
      error?.message === "Refresh Token expired"
    ) {
      error.message = NotificationConstants.REFRESH_TOKEN_FAILURE;
      throw error;
    }
    throw error;
  }
}

export function getNoAuthCallParams(method: string, headers: any, body?: any) {
  const menu = store.getState().menu;
  return {
    method,
    headers: { ...strings.applicationJSON, uxpid: menu.sessionId, ...headers },
    data: JSON.stringify(body),
  };
}

export async function getHeaderObject({
  accessToken,
  refreshToken,
  userIdToken,
  sessionId,
}: {
  accessToken: string;
  refreshToken: string;
  userIdToken: string;
  sessionId: string;
}) {
  try {
    const verifiedIdToken = await checkIdToken(
      accessToken,
      refreshToken,
      userIdToken
    );
    if (verifiedIdToken)
      return {
        "Content-Type": "application/json",
        Authorization: "Bearer " + verifiedIdToken,
        uxpid: sessionId,
      };
    else history.push(urls.landingViewPath);
  } catch (error: any) {
    store.dispatch(logOutAction());
    history.push(urls.landingViewPath);
    throw error;
  }
}

export async function makeCall(
  callName: string,
  callParams: any,
  ignoredApis: string[] = urls.POSTHOG_IGNORED_APIS
) {
  try {
    let call = api(callName, callParams);
    let timeout = getTimeoutPromise();
    const response: any = await Promise.race([timeout, call]).catch((err) => {
      throw err;
    });
    if (response.status !== 200 && response.status !== 302) {
      throw response.data;
    }
    return response.data;
  } catch (error: any) {
    // Check if the current API call is in the ignored list
    const isIgnored = ignoredApis.some((ignoredApi) =>
      callName.includes(ignoredApi)
    );

    if (!isIgnored) {
      try {
        // Capture the original error object with stack trace
        if (typeof window !== "undefined" && posthog) {
          // Capture exception first
          posthog.captureException(error, {
            $set: { has_error: true },
            api_endpoint: callName,
          });
          
          // Start recording the session context for this error
          posthog.startSessionRecording();
          
          // Force flush the data immediately to ensure it's sent before potential tab close
          // This ensures the recording buffer is uploaded
          if (typeof posthog.capture === "function") {
            posthog.capture("$session_recording_started_on_error", {
              api_endpoint: callName,
              error_message: error?.message || "Unknown error",
            });
          }
        }
      } catch (posthogError) {
        // Silently fail if PostHog is not available
        console.error("PostHog error capture failed:", posthogError);
      }
    }

    throw error?.response?.data ?? notifiers.GENERIC_ERROR;
  }
}

export function getTimeoutPromise() {
  return new Promise((resolve, reject) => {
    setTimeout(reject, 25000, { error: true, timeout: true });
  });
}

export async function checkIdToken(
  accessToken: string,
  refreshTok: string,
  userIdToken: string,
  initialStartup = false
) {
  if (userIdToken) {
    const decodedIdToken: any = jwt_decode(userIdToken);
    const decodedRefreshToken: any = jwt_decode(refreshTok);
    let diff = decodedIdToken["exp"] * 1000 - Date.now();
    let diffRefresh = decodedRefreshToken["exp"] * 1000 - Date.now();
    let cap = 120000;
    let userId = decodedIdToken["userId"];
    if (userId && diff <= cap) {
      if (diffRefresh < 0) {
        throw { error: true, message: "Refresh Token expired" };
      }
      try {
        const newUserIdToken = await refreshToken(refreshTok, userIdToken);
        return newUserIdToken;
      } catch (error: any) {
        throw error;
      }
    } else {
      if (initialStartup) {
        store.dispatch(
          updateTokens({
            refreshToken: refreshTok,
            userIdToken,
          })
        );
      }
      return userIdToken;
    }
  }
}

export async function refreshToken(refreshToken: string, idToken: string) {
  const body = { refreshToken: refreshToken, idToken: idToken };
  try {
    const callParams = getNoAuthCallParams(strings.POST, body);
    const response: any = await makeCall(urls.REFRESHTOKEN, callParams)!;
    store.dispatch(
      updateTokens({
        refreshToken: response["refreshToken"],
        userIdToken: response["idToken"],
      })
    );
    return response["idToken"];
  } catch (error: any) {
    throw error;
  }
}

export async function ErrorBoundaryCall(body: any) {
  try {
    const callParams = await getCallParams(strings.POST, {}, body);
    await makeCall(urls.ERRORBOUNDARY, callParams);
  } catch (error: any) {
    throw error;
  }
}
